import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { Input, Modal, Switch, message } from 'antd';
import { DragDropContext } from '@hello-pangea/dnd';

import { useAdjustTagUsageCount } from '@hooks/CachedLists/UseTagCatalog';

import EmptyState from '@components/Gui/EmptyState';
import { NwAddButton, NwButton, NwCancelButton, NwSaveButton } from '@components/Gui/NwButton';
import NwDrawer from '@components/Gui/NwDrawer';
import NwFixedCancelAndSaveBar from '@components/Gui/NwFixedCancelAndSaveBar';
import InputTag from '@components/Gui/NwForm/InputTag';
import { NwFakeForm, NwFakeFormItem } from '@components/Gui/NwForm/NwFakeFormWrapper';
import NwSegmented from '@components/Gui/NwForm/NwSegmented';
import NwMediaSelector from '@components/Gui/NwMediaSelector';

import {
    ARTICLE_TITLE_MAX_LENGTH,
    buildArticleSearchText,
    getArticleRowLayoutColumnCount,
    getArticleVideoEmbedData,
    normalizeArticleRowLayoutKey,
} from '@containers/Art/Articles/Articles.constants';
import {
    makeArticleBlock,
    makeArticleColumn,
    makeArticleMedia,
    makeArticleRow,
    makeEmbedArticleMedia,
    makeFileArticleMedia,
    makeNetwalkArticleMedia,
    makeUploadArticleMedia,
} from '@containers/Art/Articles/ArticlesRepository';
import { ART_DOCUMENT_UPLOAD_TYPE, linkArtWebVideo, uploadArtDocumentFile } from '@containers/Art/ArtDocuments';

import ArticleEditorRow from '@containers/Art/Articles/ArticleEditorDrawer/ArticleEditorRow';
import ArticlePreviewPane from '@containers/Art/Articles/ArticleEditorDrawer/ArticlePreviewPane';
import ArticleRowLayoutModal from '@containers/Art/Articles/ArticleEditorDrawer/ArticleRowLayoutModal';
import {
    getInitialRows,
    moveArticleBlock,
    moveItem,
    normalizeTitle,
    parseArticleBlockDroppableId,
    prepareArticleRowsForSave,
    refreshRowsPositions,
    sortByPosition,
} from '@containers/Art/Articles/ArticleEditorDrawer/articleEditorDrawerHelpers';
import styled from 'styled-components';

import {
    ArticleContentEmptyRowsBox,
    ArticleContentHeader,
    ArticleContentLabel,
    ArticleEditorEditBodyWrap,
    ArticleRowAddToolbar,
    ArticleRowAddToolbarBelow,
    ArticleRowsList,
    ArticleSaveActions,
    UnsavedChangesFooter,
    UnsavedChangesMessage,
} from '@containers/Art/Articles/ArticleEditorDrawer/ArticleEditorLayout.styles';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

// Title, Tags, Published on a single row; Published column is auto-sized
const ContentHeaderFields = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) auto;
    gap: 1rem;
    align-items: start;

    @media (max-width: 640px) {
        grid-template-columns: minmax(0, 1fr);
    }
`;

const CONTENT_DRAWER_VIEW = {
    EDIT: 'edit',
    PREVIEW: 'preview',
};

const DRAWER_VIEW_SEGMENTS = [
    { label: 'Preview', value: CONTENT_DRAWER_VIEW.PREVIEW },
    { label: 'Edit', value: CONTENT_DRAWER_VIEW.EDIT },
];

const normalizeInitialView = (value) =>
    value === CONTENT_DRAWER_VIEW.PREVIEW ? CONTENT_DRAWER_VIEW.PREVIEW : CONTENT_DRAWER_VIEW.EDIT;

const getInitialEditorView = ({ canManageContent, initialView, modelContent }) => {
    if (!canManageContent) return CONTENT_DRAWER_VIEW.PREVIEW;
    if (!modelContent) return CONTENT_DRAWER_VIEW.EDIT;
    return normalizeInitialView(initialView);
};

/** Parse Rows from API response (may arrive as JSON string or as an array). */
const parseRowsFromApiValue = (rowsValue) => {
    if (Array.isArray(rowsValue)) return rowsValue;
    if (!rowsValue || rowsValue === '[]') return [];
    try {
        const parsed = JSON.parse(rowsValue);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

const getInitialModelContentRows = (content) => {
    if (!content) return [];
    const rows = parseRowsFromApiValue(content.Rows);
    return getInitialRows({ Rows: rows });
};

const ModelContentEditorDrawer = ({
    canManageContent = true,
    modelContent,
    modelID,
    initialView = 'edit',
    onClose,
    onSave,
    saving,
    zIndex,
}) => {
    const [title, setTitle] = useState(() => (modelContent ? modelContent.Title : '') || '');
    const [rows, setRows] = useState(() => getInitialModelContentRows(modelContent));
    const [published, setPublished] = useState(() => Boolean(modelContent?.Published));
    const [tagIDs, setTagIDs] = useState(() => (modelContent?.TagIDs || []).map(Number));
    const [mediaSelector, setMediaSelector] = useState();
    const [readingMediaCount, setReadingMediaCount] = useState(0);
    const [touchForm, setTouchForm] = useState(false);
    const [view, setView] = useState(() =>
        getInitialEditorView({ canManageContent, initialView, modelContent })
    );
    const [showValidationErrors, setShowValidationErrors] = useState(false);
    const [unsavedCloseModalOpen, setUnsavedCloseModalOpen] = useState(false);
    const [rowLayoutModal, setRowLayoutModal] = useState(null);

    const adjustTagUsageCount = useAdjustTagUsageCount();

    const prevContentStableIdRef = useRef(undefined);
    const pendingScrollToNewBlockIdRef = useRef(null);
    const drawerContentAnchorRef = useRef(null);
    // tag gia' conteggiati sul catalogo: il drawer resta aperto dopo il salvataggio, quindi
    // il confronto va fatto con l'ultimo stato salvato e non con la prop di partenza
    const persistedTagIdsRef = useRef((modelContent?.TagIDs || []).map(Number));

    const sortedRows = useMemo(() => refreshRowsPositions(sortByPosition(rows)), [rows]);

    const previewContentSnapshot = useMemo(
        () => ({
            ...(modelContent || {}),
            Rows: sortedRows,
            Title: title,
        }),
        [modelContent, sortedRows, title]
    );

    const titleError = useMemo(() => {
        if (!showValidationErrors) return null;
        return normalizeTitle(title) ? null : 'Title is required';
    }, [showValidationErrors, title]);

    // Reset view when switching to a different content item
    useEffect(() => {
        const prevId = prevContentStableIdRef.current;
        const nextId = modelContent?.ID ?? null;

        if (prevId === undefined) {
            setView(getInitialEditorView({ canManageContent, initialView, modelContent }));
            prevContentStableIdRef.current = nextId;
            return;
        }

        prevContentStableIdRef.current = nextId;

        const draftJustPersisted = prevId === null && nextId != null;
        if (draftJustPersisted) return;

        if (prevId !== nextId) {
            setView(getInitialEditorView({ canManageContent, initialView, modelContent }));
        }
    }, [canManageContent, modelContent, initialView]);

    // Sync form fields when content prop changes
    useEffect(() => {
        pendingScrollToNewBlockIdRef.current = null;
        setTitle((modelContent ? modelContent.Title : '') || '');
        setRows(getInitialModelContentRows(modelContent));
        setPublished(Boolean(modelContent?.Published));
        setTagIDs((modelContent?.TagIDs || []).map(Number));
        persistedTagIdsRef.current = (modelContent?.TagIDs || []).map(Number);
        setTouchForm(false);
        setShowValidationErrors(false);
        setRowLayoutModal(null);
    }, [modelContent]);

    useLayoutEffect(() => {
        const blockId = pendingScrollToNewBlockIdRef.current;
        if (!blockId) return;
        pendingScrollToNewBlockIdRef.current = null;
        const selector = `[data-article-editor-block-id="${CSS.escape(String(blockId))}"]`;
        const el = typeof document !== 'undefined' ? document.querySelector(selector) : null;
        if (el && typeof el.scrollIntoView === 'function') {
            el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
        }
    }, [rows]);

    useLayoutEffect(() => {
        const anchor = drawerContentAnchorRef.current;
        const drawerBody = anchor?.parentElement;
        if (!drawerBody) return;
        drawerBody.scrollTop = 0;
        drawerBody.scrollLeft = 0;
    }, [view]);

    const markTouched = () => setTouchForm(true);

    const updateRows = (updater) => {
        markTouched();
        setRows((currentRows) => {
            const nextRows = typeof updater === 'function' ? updater(currentRows) : updater;
            return refreshRowsPositions(nextRows);
        });
    };

    const updateColumn = (rowId, columnId, updater) => {
        updateRows((currentRows) =>
            currentRows.map((row) =>
                row.ID === rowId
                    ? {
                          ...row,
                          Columns: row.Columns.map((column) =>
                              column.ID === columnId ? updater(column) : column
                          ),
                      }
                    : row
            )
        );
    };

    const updateBlock = (rowId, columnId, blockId, updater) => {
        updateColumn(rowId, columnId, (column) => ({
            ...column,
            Blocks: column.Blocks.map((block) => (block.ID === blockId ? updater(block) : block)),
        }));
    };

    const handleChangeTitle = (event) => {
        markTouched();
        setTitle(event.target.value);
    };

    const handleAddRow = (layoutKey) => {
        updateRows((currentRows) => [...currentRows, makeArticleRow(layoutKey, currentRows.length + 1)]);
    };

    const handleRemoveRow = (rowId) => {
        updateRows((currentRows) => currentRows.filter((row) => row.ID !== rowId));
    };

    const handleMoveRow = (sourceIndex, destinationIndex) => {
        updateRows((currentRows) => moveItem(sortByPosition(currentRows), sourceIndex, destinationIndex));
    };

    const handleApplyRowLayout = (rowId, layoutKey) => {
        const layout = normalizeArticleRowLayoutKey(layoutKey);
        const normalizedCount = getArticleRowLayoutColumnCount(layout);

        updateRows((currentRows) =>
            currentRows.map((row) => {
                if (row.ID !== rowId) return row;

                const sortedColumns = sortByPosition(row.Columns);
                const currentLen = sortedColumns.length;

                if (normalizedCount === currentLen) {
                    if (normalizeArticleRowLayoutKey(row.Layout) === layout) return row;
                    return { ...row, ColumnCount: normalizedCount, Layout: layout };
                }

                let nextColumns;
                if (normalizedCount > currentLen) {
                    nextColumns = [
                        ...sortedColumns,
                        ...Array.from({ length: normalizedCount - currentLen }, (_, idx) =>
                            makeArticleColumn(currentLen + idx + 1)
                        ),
                    ];
                } else {
                    const kept = sortedColumns.slice(0, normalizedCount);
                    const dropped = sortedColumns.slice(normalizedCount);
                    const blocksFromDropped = dropped.flatMap((column) => sortByPosition(column.Blocks));
                    if (blocksFromDropped.length > 0 && kept.length > 0) {
                        const lastIndex = kept.length - 1;
                        kept[lastIndex] = {
                            ...kept[lastIndex],
                            Blocks: [...sortByPosition(kept[lastIndex].Blocks), ...blocksFromDropped],
                        };
                    }
                    nextColumns = kept;
                }

                return { ...row, ColumnCount: normalizedCount, Columns: nextColumns, Layout: layout };
            })
        );
    };

    const handleRowLayoutPicked = (layoutKey) => {
        if (!rowLayoutModal) return;
        if (rowLayoutModal.mode === 'add') {
            handleAddRow(layoutKey);
        } else {
            handleApplyRowLayout(rowLayoutModal.rowId, layoutKey);
        }
    };

    const handleAddBlock = (rowId, columnId, blockType) => {
        updateColumn(rowId, columnId, (column) => {
            const newBlock = makeArticleBlock(blockType, column.Blocks.length + 1);
            pendingScrollToNewBlockIdRef.current = newBlock.ID;
            return { ...column, Blocks: [...column.Blocks, newBlock] };
        });
    };

    const handleRemoveBlock = (rowId, columnId, blockId) => {
        updateColumn(rowId, columnId, (column) => ({
            ...column,
            Blocks: column.Blocks.filter((block) => block.ID !== blockId),
        }));
    };

    const handleDragBlockEnd = (result) => {
        const { destination, source } = result;
        if (!destination) return;
        if (source.droppableId === destination.droppableId && source.index === destination.index) return;

        const sourceLocation = parseArticleBlockDroppableId(source.droppableId);
        const destinationLocation = parseArticleBlockDroppableId(destination.droppableId);
        if (!sourceLocation || !destinationLocation) return;

        updateRows((currentRows) =>
            moveArticleBlock(currentRows, sourceLocation, destinationLocation, source.index, destination.index)
        );
    };

    const handleChangeBlockTitle = (rowId, columnId, blockId, value) => {
        updateBlock(rowId, columnId, blockId, (block) => ({ ...block, Title: value }));
    };

    const handleChangeBlockText = (rowId, columnId, blockId, value) => {
        updateBlock(rowId, columnId, blockId, (block) => ({ ...block, Text: value }));
    };

    const handleChangeMedia = (rowId, columnId, blockId, updater) => {
        updateBlock(rowId, columnId, blockId, (block) => {
            const currentMedia = block.Media || makeArticleMedia();
            return {
                ...block,
                Media: typeof updater === 'function' ? updater(currentMedia) : updater,
            };
        });
    };

    const handleChangeMediaSource = (rowId, columnId, blockId, source) => {
        handleChangeMedia(rowId, columnId, blockId, makeArticleMedia({ Source: source }));
    };

    const handleUploadMedia = (rowId, columnId, blockId) => async (uploadInfo) => {
        const file = uploadInfo.file;
        setReadingMediaCount((c) => c + 1);
        try {
            if (!file.type || (!file.type.startsWith('image/') && !file.type.startsWith('video/'))) {
                message.error('Please select an image or video file');
                return;
            }
            const document = await uploadArtDocumentFile(file);
            handleChangeMedia(rowId, columnId, blockId, makeUploadArticleMedia({ document }));
        } catch (error) {
            message.error('Unable to read content media');
            throw error;
        } finally {
            setReadingMediaCount((c) => Math.max(0, c - 1));
        }
    };

    const handleUploadFile = (rowId, columnId, blockId) => async (uploadInfo) => {
        const file = uploadInfo.file;
        setReadingMediaCount((c) => c + 1);
        try {
            if (file.type !== 'application/pdf') {
                message.error('Please select a PDF file');
                return;
            }
            const document = await uploadArtDocumentFile(file, ART_DOCUMENT_UPLOAD_TYPE.FILE);
            handleChangeMedia(rowId, columnId, blockId, (currentMedia) =>
                makeFileArticleMedia({ document, linkText: currentMedia.LinkText })
            );
        } catch (error) {
            message.error('Unable to read linked file');
            throw error;
        } finally {
            setReadingMediaCount((c) => Math.max(0, c - 1));
        }
    };

    const handleChangeLinkText = (rowId, columnId, blockId, value) => {
        handleChangeMedia(rowId, columnId, blockId, (media) => ({ ...media, LinkText: value }));
    };

    const handleChangeNetwalkModel = (rowId, columnId, blockId, modelId) => {
        handleChangeMedia(rowId, columnId, blockId, (media) =>
            makeArticleMedia({ ...media, DocumentID: undefined, ModelId: modelId, Source: 'netwalk' })
        );
    };

    const handleSelectNetwalkMedia = (media) => {
        if (!mediaSelector) return;
        handleChangeMedia(
            mediaSelector.rowId,
            mediaSelector.columnId,
            mediaSelector.blockId,
            makeNetwalkArticleMedia({ media, modelId: mediaSelector.modelId })
        );
        setMediaSelector();
    };

    const handleSelectUploadedMedia = (rowId, columnId, blockId, upload) => {
        handleChangeMedia(rowId, columnId, blockId, makeUploadArticleMedia({ document: upload }));
    };

    const handleChangeEmbedInput = (rowId, columnId, blockId, embedInput) => {
        handleChangeMedia(rowId, columnId, blockId, (media) =>
            makeArticleMedia({ ...media, DocumentID: undefined, EmbedInput: embedInput, Source: 'embed' })
        );
    };

    const handleApplyEmbedMedia = async (rowId, columnId, blockId, embedInput) => {
        const embedData = getArticleVideoEmbedData(embedInput);
        if (!embedData) {
            message.error('Please enter a valid Vimeo or YouTube URL');
            return;
        }
        setReadingMediaCount((c) => c + 1);
        try {
            const document = await linkArtWebVideo({ link: embedData.url, title: 'Content video' });
            handleChangeMedia(rowId, columnId, blockId, makeEmbedArticleMedia({ document }));
        } catch (error) {
            message.error('Unable to link content video');
            throw error;
        } finally {
            setReadingMediaCount((c) => Math.max(0, c - 1));
        }
    };

    const persistContent = async () => {
        setShowValidationErrors(true);

        const normalizedTitle = normalizeTitle(title);
        if (!normalizedTitle) return false;

        if (readingMediaCount > 0) {
            message.error('Please wait for media upload to finish');
            return false;
        }

        let preparedRows;
        try {
            preparedRows = prepareArticleRowsForSave(sortedRows);
        } catch (error) {
            message.error(error.message);
            return false;
        }

        try {
            await onSave({
                ...(modelContent || {}),
                ID: modelContent?.ID,
                ModelID: modelID,
                Title: normalizedTitle,
                Rows: JSON.stringify(preparedRows),
                SearchText: buildArticleSearchText({ Rows: preparedRows, Title: normalizedTitle }),
                Published: published,
                TagIDs: tagIDs,
            });
        } catch {
            return false;
        }

        // i tag stanno dentro il record del contenuto e non passano da endpoint di
        // associazione: il contatore del catalogo lo muove qui la GUI, come per le news
        const previousTagIds = persistedTagIdsRef.current;
        const nextTagIds = tagIDs.map(Number);
        previousTagIds
            .filter((tagId) => !nextTagIds.includes(tagId))
            .forEach((tagId) => adjustTagUsageCount(tagId, -1));
        nextTagIds.filter((tagId) => !previousTagIds.includes(tagId)).forEach((tagId) => adjustTagUsageCount(tagId, 1));
        persistedTagIdsRef.current = nextTagIds;

        setTouchForm(false);
        setShowValidationErrors(false);
        setView(CONTENT_DRAWER_VIEW.PREVIEW);
        return true;
    };

    const requestClose = () => {
        if (!touchForm) {
            onClose();
            return;
        }
        setUnsavedCloseModalOpen(true);
    };

    const handleModalDiscardAndClose = () => {
        setUnsavedCloseModalOpen(false);
        setTouchForm(false);
        onClose();
    };

    const handleModalSave = async () => {
        const hasTitleError = !normalizeTitle(title);
        const ok = await persistContent();
        if (!ok) {
            if (hasTitleError) setUnsavedCloseModalOpen(false);
            return;
        }
        setUnsavedCloseModalOpen(false);
        onClose();
    };

    const handleToolbarSave = () => {
        void persistContent();
    };

    const drawerExtraNode = (
        <NwSegmented
            options={canManageContent ? DRAWER_VIEW_SEGMENTS : [{ label: 'Preview', value: CONTENT_DRAWER_VIEW.PREVIEW }]}
            value={view}
            onChange={(nextView) => setView(normalizeInitialView(nextView))}
        />
    );

    const rowLayoutModalActiveLayout =
        rowLayoutModal?.mode === 'edit'
            ? normalizeArticleRowLayoutKey(sortedRows.find((r) => r.ID === rowLayoutModal.rowId)?.Layout)
            : undefined;

    const rowCallbacks = {
        onAddBlock: handleAddBlock,
        onApplyEmbedMedia: handleApplyEmbedMedia,
        onChangeBlockText: handleChangeBlockText,
        onChangeBlockTitle: handleChangeBlockTitle,
        onChangeEmbedInput: handleChangeEmbedInput,
        onChangeLinkText: handleChangeLinkText,
        onChangeMediaSource: handleChangeMediaSource,
        onChangeNetwalkModel: handleChangeNetwalkModel,
        onConfigureRowLayout: (rowId) => setRowLayoutModal({ mode: 'edit', rowId }),
        onMoveRow: handleMoveRow,
        onOpenMediaSelector: setMediaSelector,
        onRemoveBlock: handleRemoveBlock,
        onRemoveRow: handleRemoveRow,
        onSelectUploadedMedia: handleSelectUploadedMedia,
        onUploadFileEnd: handleUploadFile,
        onUploadMediaEnd: handleUploadMedia,
        sortByPositionFn: sortByPosition,
        uploadedSelectorZIndex: (zIndex ?? 1040) + 20,
    };

    const addRowControl = (
        <NwAddButton
            ghost
            label="Add row"
            lowercase
            onClick={() => setRowLayoutModal({ mode: 'add' })}
        />
    );

    return (
        <>
            <NwDrawer
                touchForm={false}
                whiteBg={view !== CONTENT_DRAWER_VIEW.PREVIEW}
                width="90vw"
                title={modelContent ? 'Edit Model Content' : 'New Model Content'}
                extra={drawerExtraNode}
                zIndex={zIndex}
                onClose={requestClose}
            >
                <div ref={drawerContentAnchorRef}>
                    {view === CONTENT_DRAWER_VIEW.PREVIEW ? (
                        <ArticlePreviewPane articleSnapshot={previewContentSnapshot} />
                    ) : (
                        <>
                            <ArticleEditorEditBodyWrap>
                                <NwFakeForm>
                                    <ContentHeaderFields>
                                        <NwFakeFormItem
                                            error={titleError}
                                            label="Title"
                                            required
                                            control={
                                                <Input
                                                    autoComplete="off"
                                                    maxLength={ARTICLE_TITLE_MAX_LENGTH}
                                                    placeholder="Content title"
                                                    showCount
                                                    value={title}
                                                    onChange={handleChangeTitle}
                                                />
                                            }
                                        />

                                        <NwFakeFormItem
                                            label="Tags"
                                            control={
                                                <InputTag
                                                    contactType="ModelContent"
                                                    value={tagIDs}
                                                    onChange={(values) => {
                                                        markTouched();
                                                        setTagIDs(values || []);
                                                    }}
                                                />
                                            }
                                        />

                                        <NwFakeFormItem
                                            label="Published"
                                            control={
                                                <Switch
                                                    checked={published}
                                                    onChange={(checked) => {
                                                        markTouched();
                                                        setPublished(checked);
                                                    }}
                                                />
                                            }
                                        />
                                    </ContentHeaderFields>

                                    <ArticleContentHeader>
                                        <ArticleContentLabel>Content</ArticleContentLabel>
                                        <ArticleRowAddToolbar>{addRowControl}</ArticleRowAddToolbar>
                                    </ArticleContentHeader>

                                    <DragDropContext onDragEnd={handleDragBlockEnd}>
                                        <ArticleRowsList>
                                            {sortedRows.length ? (
                                                sortedRows.map((row, rowIndex) => (
                                                    <ArticleEditorRow
                                                        key={row.ID}
                                                        columnsSorted={sortByPosition(row.Columns)}
                                                        row={row}
                                                        rowIndex={rowIndex}
                                                        sortedRowsLength={sortedRows.length}
                                                        {...rowCallbacks}
                                                    />
                                                ))
                                            ) : (
                                                <ArticleContentEmptyRowsBox>
                                                    <EmptyState message="No rows" />
                                                    <p className="empty-rows-hint">
                                                        Click &quot;Add row&quot; above to start adding content.
                                                    </p>
                                                </ArticleContentEmptyRowsBox>
                                            )}
                                        </ArticleRowsList>
                                    </DragDropContext>

                                    {sortedRows.length > 0 && (
                                        <ArticleRowAddToolbarBelow>{addRowControl}</ArticleRowAddToolbarBelow>
                                    )}

                                    {mediaSelector && (
                                        <NwMediaSelector
                                            includePictures
                                            includeVideos
                                            modelId={mediaSelector.modelId}
                                            title="Select content media"
                                            zIndex={(zIndex ?? 1040) + 20}
                                            onClose={() => setMediaSelector()}
                                            onSelectMedia={handleSelectNetwalkMedia}
                                        />
                                    )}
                                </NwFakeForm>
                            </ArticleEditorEditBodyWrap>

                            <NwFixedCancelAndSaveBar
                                zIndex={(zIndex ?? 1040) + 2}
                                width="90vw"
                                minWidth="0"
                                left={
                                    <NwCancelButton
                                        disabled={saving || readingMediaCount > 0}
                                        onClick={requestClose}
                                    />
                                }
                                right={
                                    <ArticleSaveActions>
                                        <NwSaveButton
                                            disabled={saving || readingMediaCount > 0}
                                            htmlType="button"
                                            loading={saving || readingMediaCount > 0}
                                            onClick={handleToolbarSave}
                                        />
                                        {showValidationErrors && !normalizeTitle(title) && (
                                            <div className="article-save-error">
                                                Please review the highlighted errors before saving.
                                            </div>
                                        )}
                                    </ArticleSaveActions>
                                }
                            />
                        </>
                    )}
                </div>
            </NwDrawer>

            <ArticleRowLayoutModal
                activeLayout={rowLayoutModalActiveLayout}
                visible={Boolean(rowLayoutModal)}
                zIndex={(zIndex ?? 1040) + 40}
                onCancel={() => setRowLayoutModal(null)}
                onSelect={handleRowLayoutPicked}
            />

            <Modal
                destroyOnClose
                footer={
                    <UnsavedChangesFooter>
                        <NwCancelButton onClick={() => setUnsavedCloseModalOpen(false)} />
                        <div className="unsaved-changes-footer-right">
                            <NwButton
                                danger
                                htmlType="button"
                                icon={byPrefixAndName.fasl['trash']}
                                label="Discard changes"
                                onClick={handleModalDiscardAndClose}
                            />
                            <NwSaveButton
                                htmlType="button"
                                label="Save & close"
                                loading={saving}
                                onClick={() => void handleModalSave()}
                            />
                        </div>
                    </UnsavedChangesFooter>
                }
                title="Unsaved changes"
                visible={unsavedCloseModalOpen}
                width={560}
                onCancel={() => setUnsavedCloseModalOpen(false)}
            >
                <UnsavedChangesMessage>
                    <div>
                        <p className="unsaved-changes-text">You have unsaved changes to this content.</p>
                        <p className="unsaved-changes-hint">
                            Save them before closing, or discard them to close anyway.
                        </p>
                    </div>
                </UnsavedChangesMessage>
            </Modal>
        </>
    );
};

export default ModelContentEditorDrawer;
